// apps/web/src/views/InformeAvanceView.tsx
import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { FileText, Loader2, Calendar, BarChart3, Map, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n/i18n';

import AnalisisService, { FiltrosDisponibles } from '../services/analisisService';
import InformeAvanceService, { InformeAvanceDatos, FormatoGeo } from '../services/informeAvanceService';
import MapaAnalisis from '../components/analisis/MapaAnalisis';

// ===============================
// COMPONENTE PRINCIPAL
// ===============================

export const InformeAvanceView: React.FC = () => {
  const { t } = useTranslation();
  // Estados principales
  const [filtrosDisponibles, setFiltrosDisponibles] = useState<FiltrosDisponibles | null>(null);
  const [informeActual, setInformeActual] = useState<InformeAvanceDatos | null>(null);
  const [cargandoFiltros, setCargandoFiltros] = useState(true);
  const [cargandoInforme, setCargandoInforme] = useState(false);
  const [generandoPDF, setGenerandoPDF] = useState(false);

  // Estados del formulario
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [propietario, setPropietario] = useState<string>('');
  const [prediosSeleccionados, setPrediosSeleccionados] = useState<number[]>([]);

  // Estados para el menú de descarga geográfica (KML/KMZ)
  const [mostrarMenuGeo, setMostrarMenuGeo] = useState(false);
  const [fundirGeo, setFundirGeo] = useState(false);
  const menuGeoRef = useRef<HTMLDivElement>(null);

  // Estados para configuración de PDF
  const [mostrarConfigPDF, setMostrarConfigPDF] = useState(false);
  const [configPDF, setConfigPDF] = useState({
    zoom: 13,
    titulo: '',
    incluir_datos_area: true,
    incluir_mapas: true
  });

  // ===============================
  // EFECTOS
  // ===============================

  useEffect(() => {
    cargarFiltrosDisponibles();
  }, []);

  // Limpiar informe cuando cambien los parámetros de filtro
  useEffect(() => {
    setInformeActual(null);
  }, [fechaDesde, fechaHasta, propietario, prediosSeleccionados]);

  // Cerrar el menú de descarga geográfica al hacer click fuera
  useEffect(() => {
    if (!mostrarMenuGeo) return;

    const handleClickFuera = (event: MouseEvent) => {
      if (menuGeoRef.current && !menuGeoRef.current.contains(event.target as Node)) {
        setMostrarMenuGeo(false);
      }
    };

    document.addEventListener('mousedown', handleClickFuera);
    return () => document.removeEventListener('mousedown', handleClickFuera);
  }, [mostrarMenuGeo]);

  // ===============================
  // FUNCIONES
  // ===============================

  const cargarFiltrosDisponibles = async () => {
    try {
      setCargandoFiltros(true);
      const filtros = await AnalisisService.obtenerFiltrosDisponibles();
      setFiltrosDisponibles(filtros);

      if (filtros.rango_fechas.fecha_min && filtros.rango_fechas.fecha_max) {
        setFechaDesde(filtros.rango_fechas.fecha_min);
        setFechaHasta(filtros.rango_fechas.fecha_max);
      }
    } catch (error) {
      console.error('Error cargando filtros:', error);
      toast.error('Error cargando filtros disponibles');
    } finally {
      setCargandoFiltros(false);
    }
  };

  // Obtener propietarios únicos de los predios disponibles
  const obtenerPropietarios = (): string[] => {
    if (!filtrosDisponibles) return [];
    const propietarios = new Set<string>();
    filtrosDisponibles.predios.forEach(p => {
      if (p.propietario) {
        propietarios.add(p.propietario);
      }
    });
    return Array.from(propietarios).sort();
  };

  // Filtrar predios por propietario
  const prediosFiltrados = filtrosDisponibles?.predios.filter(
    p => !propietario || p.propietario === propietario
  ) || [];

  // Toggle de selección de predio
  const togglePredioSeleccionado = (predioId: number) => {
    setPrediosSeleccionados(prev =>
      prev.includes(predioId)
        ? prev.filter(id => id !== predioId)
        : [...prev, predioId]
    );
  };

  // Limpiar predios seleccionados cuando cambia propietario
  const handleChangePropietario = (value: string) => {
    setPropietario(value);
    setPrediosSeleccionados([]);
  };

  const validarFormulario = (): boolean => {
    if (!fechaDesde || !fechaHasta) {
      toast.error('Debes seleccionar un rango de fechas');
      return false;
    }

    if (prediosSeleccionados.length === 0) {
      toast.error('Debes seleccionar al menos un predio');
      return false;
    }

    return true;
  };

  const ejecutarInforme = async () => {
    if (!validarFormulario()) {
      return;
    }

    try {
      setCargandoInforme(true);

      const resultado = await InformeAvanceService.obtenerDatosInformeAvance({
        fecha_desde: fechaDesde,
        fecha_hasta: fechaHasta,
        predios_ids: prediosSeleccionados
      });

      setInformeActual(resultado);
      toast.success(`✅ Informe generado: ${resultado.reportes_por_predio.reduce((sum, p) => sum + p.metricas.resumen.total_reportes, 0)} reportes encontrados`);
    } catch (error: any) {
      console.error('Error ejecutando informe:', error);
      toast.error(error.message || 'Error generando informe');
    } finally {
      setCargandoInforme(false);
    }
  };

  const abrirConfiguracionPDF = () => {
    const prediosNombres = prediosSeleccionados
      .map(id => filtrosDisponibles?.predios.find(p => p.id === id)?.codigo)
      .filter(Boolean)
      .join(', ');

    setConfigPDF({
      zoom: 13,
      titulo: `Informe de Avance - ${prediosNombres || 'Predios'} - ${new Date().toLocaleDateString()}`,
      incluir_datos_area: true,
      incluir_mapas: true
    });
    setMostrarConfigPDF(true);
  };

  const generarPDF = async () => {
    if (!validarFormulario()) {
      return;
    }

    try {
      setGenerandoPDF(true);
      setMostrarConfigPDF(false);
      toast.loading('📝 Generando informe de avance...', { id: 'informe-progress' });

      await InformeAvanceService.generarInformeAvancePDF({
        fecha_desde: fechaDesde,
        fecha_hasta: fechaHasta,
        predios_ids: prediosSeleccionados,
        zoom: configPDF.zoom,
        titulo: configPDF.titulo || undefined,
        incluir_datos_area: configPDF.incluir_datos_area,
        incluir_mapas: configPDF.incluir_mapas,
        lang: i18n.language,
      });

      toast.success('✅ Informe de avance generado exitosamente', { id: 'informe-progress' });
    } catch (error: any) {
      console.error('Error generando informe:', error);
      toast.error(`❌ Error generando informe: ${error.message}`, { id: 'informe-progress' });
    } finally {
      setGenerandoPDF(false);
    }
  };

  const descargarCSV = async () => {
    if (!validarFormulario()) {
      return;
    }

    try {
      setGenerandoPDF(true);
      toast.loading('📊 Descargando CSV...', { id: 'csv-progress' });

      await InformeAvanceService.generarInformeAvanceCSV({
        fecha_desde: fechaDesde,
        fecha_hasta: fechaHasta,
        predios_ids: prediosSeleccionados
      });

      toast.success('✅ CSV descargado exitosamente', { id: 'csv-progress' });
    } catch (error: any) {
      console.error('Error descargando CSV:', error);
      toast.error(`❌ Error descargando CSV: ${error.message}`, { id: 'csv-progress' });
    } finally {
      setGenerandoPDF(false);
    }
  };

  const descargarGeo = async (formato: FormatoGeo) => {
    setMostrarMenuGeo(false);

    if (!validarFormulario()) {
      return;
    }

    try {
      setGenerandoPDF(true);
      toast.loading(`🗺️ ${t('advance_report.downloading_geo', { format: formato.toUpperCase() })}`, { id: 'geo-progress' });

      await InformeAvanceService.generarInformeAvanceGeo({
        fecha_desde: fechaDesde,
        fecha_hasta: fechaHasta,
        predios_ids: prediosSeleccionados,
        lang: i18n.language,
      }, formato, fundirGeo);

      toast.success(`✅ ${t('advance_report.geo_downloaded', { format: formato.toUpperCase() })}`, { id: 'geo-progress' });
    } catch (error: any) {
      console.error(`Error descargando ${formato.toUpperCase()}:`, error);
      toast.error(`❌ ${t('advance_report.geo_download_error', { format: formato.toUpperCase() })}: ${error.message}`, { id: 'geo-progress' });
    } finally {
      setGenerandoPDF(false);
    }
  };

  // Calcular totales globales (suma de todos los predios)
  // Suma desde las filas reales de reportes, no desde metricas.resumen del backend
  const calcularTotalGlobal = (): { reportes: number; horas: number; areaDeclarada: number; areaKml: number; areaNeta: number; combustible: number } => {
    if (!informeActual) return { reportes: 0, horas: 0, areaDeclarada: 0, areaKml: 0, areaNeta: 0, combustible: 0 };

    let reportes = 0;
    let horas = 0;
    let areaDeclarada = 0;
    let areaKml = 0;
    let areaNeta = 0;
    let combustible = 0;

    // Sumar desde las filas visibles de reportes (kml/neta null = sin KML → 0)
    informeActual.reportes_por_predio.forEach(p => {
      reportes += p.reportes.length;  // Contar número de filas
      horas += p.reportes.reduce((sum, r) => sum + r.horas, 0);
      areaDeclarada += p.reportes.reduce((sum, r) => sum + (r.area_declarada || 0), 0);
      areaKml += p.reportes.reduce((sum, r) => sum + (r.area_kml || 0), 0);
      areaNeta += p.reportes.reduce((sum, r) => sum + (r.area_neta || 0), 0);
    });

    // Sumar combustible
    informeActual.combustible_por_predio?.forEach(c => {
      combustible += c.reduce((sum, item) => sum + item.cantidad, 0);
    });

    return { reportes, horas, areaDeclarada, areaKml, areaNeta, combustible };
  };

  const totalesGlobales = calcularTotalGlobal();

  // ===============================
  // RENDER
  // ===============================

  if (cargandoFiltros) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('common.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <FileText className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-xl font-semibold text-gray-900">{t('advance_report.title')}</h1>
                <p className="text-sm text-gray-500">{t('advance_report.generate')}</p>
              </div>
            </div>

            {informeActual && (
              <div className="flex gap-2">
                <button
                  onClick={abrirConfiguracionPDF}
                  disabled={generandoPDF}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  {t('advance_report.generate_pdf')}
                </button>
                <button
                  onClick={descargarCSV}
                  disabled={generandoPDF}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  {t('advance_report.download_csv')}
                </button>
                {/* Descarga geográfica con selector de formato */}
                <div className="relative" ref={menuGeoRef}>
                  <button
                    onClick={() => setMostrarMenuGeo(prev => !prev)}
                    disabled={generandoPDF}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                  >
                    <Map className="w-4 h-4 mr-2" />
                    {t('advance_report.download_geo')}
                    <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${mostrarMenuGeo ? 'rotate-180' : ''}`} />
                  </button>

                  {mostrarMenuGeo && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg border border-gray-200 z-20 overflow-hidden">
                      <label className="flex items-start justify-between gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200 cursor-pointer select-none">
                        <span>
                          <span className="block text-sm font-medium text-gray-900">{t('advance_report.merge_geo')}</span>
                          <span className="block text-xs text-gray-500">{t('advance_report.merge_geo_hint')}</span>
                        </span>
                        <input
                          type="checkbox"
                          checked={fundirGeo}
                          onChange={(e) => setFundirGeo(e.target.checked)}
                          className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                        />
                      </label>
                      <button
                        onClick={() => descargarGeo('kml')}
                        className="w-full text-left px-4 py-3 hover:bg-amber-50 transition-colors"
                      >
                        <p className="text-sm font-medium text-gray-900">KML</p>
                        <p className="text-xs text-gray-500">{t('advance_report.kml_hint')}</p>
                      </button>
                      <button
                        onClick={() => descargarGeo('kmz')}
                        className="w-full text-left px-4 py-3 border-t border-gray-100 hover:bg-amber-50 transition-colors"
                      >
                        <p className="text-sm font-medium text-gray-900">KMZ</p>
                        <p className="text-xs text-gray-500">{t('advance_report.kmz_hint')}</p>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Overlay de carga para PDF */}
      {generandoPDF && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 shadow-2xl max-w-md w-full mx-4">
            <div className="text-center">
              <div className="mx-auto mb-4">
                <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Generando Informe de Avance
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Procesando datos del predio seleccionado...
              </p>
              <div className="bg-gray-200 rounded-full h-2 mb-2">
                <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: '100%' }}></div>
              </div>
              <p className="text-xs text-gray-500">
                Este proceso puede tomar unos momentos
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Configuración PDF */}
      {mostrarConfigPDF && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-2xl max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              📊 Configuración del PDF
            </h3>

            <div className="space-y-4">
              {/* Título personalizado */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Título del PDF:
                </label>
                <input
                  type="text"
                  value={configPDF.titulo}
                  onChange={(e) => setConfigPDF(prev => ({ ...prev, titulo: e.target.value }))}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                  placeholder="Título personalizado..."
                />
              </div>

              {/* Control de Zoom */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nivel de Zoom del Mapa: {configPDF.zoom}
                </label>
                <input
                  type="range"
                  min="8"
                  max="18"
                  value={configPDF.zoom}
                  onChange={(e) => setConfigPDF(prev => ({ ...prev, zoom: parseInt(e.target.value) }))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>8 (Más lejos)</span>
                  <span>13 (Recomendado)</span>
                  <span>18 (Más cerca)</span>
                </div>
              </div>

              {/* Opciones de contenido */}
              <div className="border-t pt-4 mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Opciones de Contenido
                </label>
                <div className="space-y-3">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={configPDF.incluir_datos_area}
                      onChange={(e) => setConfigPDF(prev => ({ ...prev, incluir_datos_area: e.target.checked }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{t('analysis.include_metrics')}</span>
                  </label>
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={configPDF.incluir_mapas}
                      onChange={(e) => setConfigPDF(prev => ({ ...prev, incluir_mapas: e.target.checked }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{t('analysis.include_map')}</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Botones */}
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setMostrarConfigPDF(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>

              <button
                onClick={generarPDF}
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
              >
                📝 Generar PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contenido Principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Panel de Filtros */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">{t('pdf.options_title')}</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Selector de Propietario */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('advance_report.owner')}
              </label>
              <select
                value={propietario}
                onChange={(e) => handleChangePropietario(e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">{t('common.all')}</option>
                {obtenerPropietarios().map((prop) => (
                  <option key={prop} value={prop}>
                    {prop}
                  </option>
                ))}
              </select>
            </div>

            {/* Fecha Desde */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('advance_report.date_from')} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                <Calendar className="absolute right-3 top-2.5 h-5 w-5 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Fecha Hasta */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('advance_report.date_to')} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                <Calendar className="absolute right-3 top-2.5 h-5 w-5 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Selector de Predios */}
          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              {t('advance_report.select_predios')} <span className="text-red-500">*</span> ({prediosSeleccionados.length} {t('advance_report.selected')})
            </label>
            {prediosFiltrados.length === 0 ? (
              <p className="text-sm text-gray-500">
                {propietario ? t('faenas.no_predios') : t('faenas.select_predio_first')}
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {prediosFiltrados.map((predio) => (
                  <label key={predio.id} className="flex items-center space-x-2 p-3 border border-gray-200 rounded-lg hover:bg-blue-50 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={prediosSeleccionados.includes(predio.id)}
                      onChange={() => togglePredioSeleccionado(predio.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{predio.codigo}</p>
                      <p className="text-xs text-gray-500">{predio.descripcion}</p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Botón Ejecutar */}
          <div className="flex justify-end mt-4">
            <button
              onClick={ejecutarInforme}
              disabled={cargandoInforme}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {cargandoInforme ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('advance_report.generating')}
                </>
              ) : (
                <>
                  <BarChart3 className="w-4 h-4 mr-2" />
                  {t('advance_report.generate')}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Resultados */}
        {informeActual ? (
          <div className="space-y-6">
            {/* Resumen Global */}
            {prediosSeleccionados.length > 1 && (
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg shadow-sm border border-blue-200 p-6">
                <h3 className="text-lg font-medium text-blue-900 mb-4">📊 {t('advance_report.global_summary')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-white p-4 rounded-lg">
                    <p className="text-sm text-gray-600 font-medium">{t('advance_report.predios_with_data')}</p>
                    <p className="text-2xl font-bold text-blue-900">{informeActual.reportes_por_predio.length}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg">
                    <p className="text-sm text-green-600 font-medium">{t('analysis.total_reports')}</p>
                    <p className="text-2xl font-bold text-green-900">{totalesGlobales.reportes}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg">
                    <p className="text-sm text-purple-600 font-medium">{t('advance_report.total_hours')}</p>
                    <p className="text-2xl font-bold text-purple-900">{totalesGlobales.horas.toFixed(2)}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg">
                    <p className="text-sm text-orange-600 font-medium">{t('advance_report.total_area_label')}</p>
                    <div className="mt-1 space-y-0.5 text-sm">
                      <p><span className="text-gray-500">{t('advance_report.col_declared')}:</span> <span className="font-semibold text-orange-900">{totalesGlobales.areaDeclarada.toFixed(2)} ha</span></p>
                      <p><span className="text-gray-500">{t('advance_report.col_kml')}:</span> <span className="font-semibold text-orange-900">{totalesGlobales.areaKml.toFixed(2)} ha</span></p>
                      <p><span className="text-gray-500">{t('advance_report.col_net')}:</span> <span className="font-semibold text-orange-900">{totalesGlobales.areaNeta.toFixed(2)} ha</span></p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Informe por Predio */}
            {informeActual.reportes_por_predio.map((infomePredio, predioIndex) => (
              <div key={infomePredio.predio.id} className="space-y-4">
                {/* Separador de Predio */}
                {predioIndex > 0 && <div className="border-t-4 border-gray-300 my-8"></div>}

                {/* Resumen del Predio */}
                {(() => {
                  // Calcular totales desde las filas visibles en la tabla
                  const totalReportes = infomePredio.reportes.length;
                  const totalHoras = infomePredio.reportes.reduce((sum, r) => sum + r.horas, 0);
                  const totalDeclarada = infomePredio.reportes.reduce((sum, r) => sum + (r.area_declarada || 0), 0);
                  const totalKml = infomePredio.reportes.reduce((sum, r) => sum + (r.area_kml || 0), 0);
                  const totalNeta = infomePredio.reportes.reduce((sum, r) => sum + (r.area_neta || 0), 0);

                  return (
                    <div className="bg-white rounded-lg shadow-sm border p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">{t('advance_report.predio_section', { number: predioIndex + 1 })}: {infomePredio.predio.codigo}</h3>
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <p className="text-sm text-blue-600 font-medium">{t('common.code')}</p>
                          <p className="text-lg font-semibold text-blue-900">{infomePredio.predio.codigo}</p>
                        </div>
                        <div className="bg-green-50 p-4 rounded-lg">
                          <p className="text-sm text-green-600 font-medium">{t('analysis.total_reports')}</p>
                          <p className="text-2xl font-bold text-green-900">{totalReportes}</p>
                        </div>
                        <div className="bg-purple-50 p-4 rounded-lg">
                          <p className="text-sm text-purple-600 font-medium">{t('advance_report.total_hours')}</p>
                          <p className="text-2xl font-bold text-purple-900">{totalHoras.toFixed(2)}</p>
                        </div>
                        <div className="bg-orange-50 p-4 rounded-lg">
                          <p className="text-sm text-orange-600 font-medium">{t('advance_report.total_area_label')}</p>
                          <div className="mt-1 space-y-0.5 text-sm">
                            <p><span className="text-gray-500">{t('advance_report.col_declared')}:</span> <span className="font-semibold text-orange-900">{totalDeclarada.toFixed(2)} ha</span></p>
                            <p><span className="text-gray-500">{t('advance_report.col_kml')}:</span> <span className="font-semibold text-orange-900">{totalKml.toFixed(2)} ha</span></p>
                            <p><span className="text-gray-500">{t('advance_report.col_net')}:</span> <span className="font-semibold text-orange-900">{totalNeta.toFixed(2)} ha</span></p>
                          </div>
                        </div>
                        <div className="bg-red-50 p-4 rounded-lg">
                          <p className="text-sm text-red-600 font-medium">{t('advance_report.owner_label')}</p>
                          <p className="text-lg font-semibold text-red-900">{infomePredio.predio.propietario || '-'}</p>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Mapa */}
                <MapaAnalisis
                  geometrias={infomePredio.geometrias}
                  centroMapa={infomePredio.centro_mapa}
                />

                {/* Tabla de Reportes */}
                <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                  <div className="bg-gray-50 px-6 py-3 border-b">
                    <h3 className="text-lg font-medium text-gray-900">{t('advance_report.report_detail_section')}</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th rowSpan={2} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase align-bottom">{t('reports.report_number')}</th>
                          <th rowSpan={2} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase align-bottom">{t('reports.date')}</th>
                          <th rowSpan={2} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase align-bottom">{t('reports.machine')}</th>
                          <th rowSpan={2} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase align-bottom">{t('reports.operator')}</th>
                          <th rowSpan={2} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase align-bottom">{t('reports.initial_horometer')}</th>
                          <th rowSpan={2} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase align-bottom">{t('reports.final_horometer')}</th>
                          <th rowSpan={2} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase align-bottom">{t('faenas.faena')}</th>
                          <th rowSpan={2} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase align-bottom">{t('common.hours')}</th>
                          <th colSpan={3} className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase border-l border-gray-200">{t('advance_report.area_group')}</th>
                          <th colSpan={3} className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase border-l border-gray-200">{t('advance_report.yield_group')}</th>
                        </tr>
                        <tr>
                          <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 border-l border-gray-200">{t('advance_report.col_declared')}</th>
                          <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">{t('advance_report.col_kml')}</th>
                          <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">{t('advance_report.col_net')}</th>
                          <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 border-l border-gray-200">{t('advance_report.col_declared')}</th>
                          <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">{t('advance_report.col_kml')}</th>
                          <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">{t('advance_report.col_net')}</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {infomePredio.reportes.map((reporte, idx) => {
                          // '—' cuando no hay dato (faena sin KML).
                          const fmt = (v: number | null | undefined) => (v === null || v === undefined ? '—' : v.toFixed(2));
                          return (
                            <tr key={idx} className={`hover:bg-gray-50 ${reporte.tiene_superposicion ? 'bg-amber-50' : ''}`}>
                              <td className="px-3 py-2 text-xs text-gray-900">
                                {reporte.numero}
                                {reporte.tiene_superposicion && (
                                  <span
                                    className="ml-1 text-amber-600"
                                    title={t('kml.overlap_warning_short', {
                                      area: reporte.superposicion_area_hectareas?.toFixed(2),
                                    })}
                                  >
                                    ⚠️
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-xs text-gray-600">{reporte.fecha_str}</td>
                              <td className="px-3 py-2 text-xs text-gray-900">{reporte.maquina_descripcion}</td>
                              <td className="px-3 py-2 text-xs text-gray-600">{reporte.operador_descripcion}</td>
                              <td className="px-3 py-2 text-xs text-gray-900 text-center">{reporte.horometro_inicial}</td>
                              <td className="px-3 py-2 text-xs text-gray-900 text-center">{reporte.horometro_final}</td>
                              <td className="px-3 py-2 text-xs text-gray-600">{reporte.faena_descripcion}</td>
                              <td className="px-3 py-2 text-xs text-gray-900 text-right">{reporte.horas.toFixed(2)}</td>
                              <td className="px-2 py-2 text-xs text-gray-900 text-right border-l border-gray-100">{fmt(reporte.area_declarada)}</td>
                              <td className="px-2 py-2 text-xs text-gray-900 text-right">{fmt(reporte.area_kml)}</td>
                              <td className="px-2 py-2 text-xs text-gray-900 text-right">{fmt(reporte.area_neta)}</td>
                              <td className="px-2 py-2 text-xs text-gray-900 text-right border-l border-gray-100">{fmt(reporte.rendimiento_declarada)}</td>
                              <td className="px-2 py-2 text-xs text-gray-900 text-right">{fmt(reporte.rendimiento_kml)}</td>
                              <td className="px-2 py-2 text-xs text-gray-900 text-right">{fmt(reporte.rendimiento_neta)}</td>
                            </tr>
                          );
                        })}
                        {/* Fila de totales */}
                        {infomePredio.reportes.length > 0 && (() => {
                          // Calcular totales desde las filas visibles (suma de faenas)
                          const totalHoras = infomePredio.reportes.reduce((sum, r) => sum + r.horas, 0);
                          const sumDecl = infomePredio.reportes.reduce((sum, r) => sum + (r.area_declarada || 0), 0);
                          const sumKml = infomePredio.reportes.reduce((sum, r) => sum + (r.area_kml || 0), 0);
                          const sumNeta = infomePredio.reportes.reduce((sum, r) => sum + (r.area_neta || 0), 0);
                          const rend = (area: number) => (totalHoras > 0 ? (area / totalHoras).toFixed(2) : '0.00');
                          return (
                            <tr className="bg-gray-100 font-bold border-t-2 border-gray-400">
                              <td colSpan={7} className="px-3 py-2 text-xs text-gray-900 text-right">TOTAL</td>
                              <td className="px-3 py-2 text-xs text-gray-900 text-right">{totalHoras.toFixed(2)}</td>
                              <td className="px-2 py-2 text-xs text-gray-900 text-right border-l border-gray-200">{sumDecl.toFixed(2)}</td>
                              <td className="px-2 py-2 text-xs text-gray-900 text-right">{sumKml.toFixed(2)}</td>
                              <td className="px-2 py-2 text-xs text-gray-900 text-right">{sumNeta.toFixed(2)}</td>
                              <td className="px-2 py-2 text-xs text-gray-900 text-right border-l border-gray-200">{rend(sumDecl)}</td>
                              <td className="px-2 py-2 text-xs text-gray-900 text-right">{rend(sumKml)}</td>
                              <td className="px-2 py-2 text-xs text-gray-900 text-right">{rend(sumNeta)}</td>
                            </tr>
                          );
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Tabla de Combustible */}
                {infomePredio.combustible && infomePredio.combustible.length > 0 && (
                  <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                    <div className="bg-gray-50 px-6 py-3 border-b">
                      <h3 className="text-lg font-medium text-gray-900">{t('advance_report.fuel_section')}</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('reports.report_number')}</th>
                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('reports.date')}</th>
                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('reports.machine')}</th>
                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('reports.operator')}</th>
                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('reports.initial_horometer')}</th>
                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.quantity')}</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {infomePredio.combustible.map((combustible, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-3 py-2 text-xs text-gray-900">{combustible.numero}</td>
                              <td className="px-3 py-2 text-xs text-gray-600">{combustible.fecha_str}</td>
                              <td className="px-3 py-2 text-xs text-gray-900">{combustible.maquina_descripcion}</td>
                              <td className="px-3 py-2 text-xs text-gray-600">{combustible.operador_descripcion}</td>
                              <td className="px-3 py-2 text-xs text-gray-900 text-center">{combustible.horometro_inicial}</td>
                              <td className="px-3 py-2 text-xs text-gray-900 text-right">{combustible.cantidad.toFixed(2)} {combustible.unidad}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border p-12">
            <div className="text-center">
              <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('advance_report.title')}</h3>
              <p className="text-sm text-gray-500 mb-6">
                {t('advance_report.no_reports')}
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-800 mb-2">💡 {t('advance_report.pdf_content_hint')}:</h4>
                <ul className="text-xs text-blue-700 space-y-1 text-left max-w-md mx-auto">
                  <li>• {t('advance_report.pdf_page1')}</li>
                  <li>• {t('advance_report.pdf_page2')}</li>
                  <li>• {t('advance_report.pdf_page3')}</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InformeAvanceView;
